// Error Boundary global — captura erros de RENDER que os listeners de
// `window 'error'`/`unhandledrejection` (sw-recovery) não pegam de forma
// determinística.
//
// Caso crítico: lazy import de rota (React.lazy em routes.tsx) que falha com
// ChunkLoadError pós-deploy (index velho pedindo chunk hasheado que sumiu do
// nginx). O React captura a promise rejeitada do Suspense e RE-LANÇA durante o
// render — sem um boundary, a árvore desmonta e o user vê tela branca. Aqui:
//
//   1. Erro recuperável (chunk/SW) → dispara recovery silencioso (unregister +
//      clear caches + 1 reload), GATED pelo mesmo anti-loop do sw-recovery
//      (runRecoveryIfNotRecent → isRecentlyRecovered). Enquanto reloada,
//      mostra só um spinner (LoadingScreen) — sem flash de tela de erro.
//   2. Anti-loop suprimiu o reload (recovery recente) OU erro NÃO recuperável
//      → fallback navegável: mensagem + botão nativo "Recarregar" com autoFocus.
//
// Botão HTML NATIVO de propósito (não o <Button> do design system): o erro
// pode ter derrubado o contexto de spatial navigation, então o botão não pode
// depender do Norigin pra ser alcançado — `autoFocus` põe o foco REAL do DOM
// nele, sem passar por árvore de foco nenhuma.
//
// ⚠️ MAS RECEBER O FOCO NÃO ERA RECEBER O OK. Este comentário afirmava, até a
// v2.34.0, que o botão "responde a Enter sem depender do Norigin" — e isso
// nunca foi verdade. O Norigin escuta `keydown` no window e cancela a ação
// padrão de TODA tecla do keymap (setas E Enter) ANTES de olhar se há foco
// lógico; cancelar a ação padrão de um Enter é exatamente cancelar a activation
// behavior do <button>, isto é, o `click` nunca nasce. Em produção o Fernando
// viu o que isso significa: a tela de crash aparece, o botão aparece FOCADO, e
// o OK do controle não faz nada. Escape ilusório — pior que escape ausente,
// porque quem vê o botão para de procurar outra saída.
//
// Por isso o OK desta tela tem DONO EXPLÍCITO (`useOkKeyOwner`, capture no
// window, antes do Norigin). O `onClick` continua: é o caminho do mouse no web.
// jsdom não implementa activation behavior de teclado, então este buraco é
// invisível pra teste de unidade — quem o mede em Chromium de verdade é
// scripts/compat/recovery-escape-probe.mjs.

import { useTranslation } from '@app-iptv/i18n'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import { useOkKeyOwner } from '../lib/ok-key-owner'
import { isRecoverableError, runRecovery, runRecoveryIfNotRecent } from '../sw-recovery'

import { LoadingScreen } from './LoadingScreen'

interface Props {
  children: ReactNode
}

// Sentinela de observabilidade: inicializa o global de diagnóstico já no boot
// (import do módulo, que é eager pois o boundary envolve o app). Assim dá pra
// confirmar via CDP que ESTE bundle instrumentado está no ar ANTES de qualquer
// crash: bundle novo → `null` (ou objeto após crash); bundle antigo →
// `undefined`. Prod-safe: só leitura de diagnóstico, invisível ao usuário.
try {
  const w = window as unknown as { __lastRecoveryError?: unknown }
  if (typeof w.__lastRecoveryError === 'undefined') w.__lastRecoveryError = null
} catch {
  // window pode não existir (SSR/teste) — sentinela é best-effort.
}

interface State {
  /** 'ok' = sem erro; 'recovering' = recovery silencioso em curso (spinner);
   *  'failed' = fallback visível (anti-loop suprimiu ou erro não recuperável). */
  phase: 'ok' | 'recovering' | 'failed'
}

export class RecoveryBoundary extends Component<Props, State> {
  override state: State = { phase: 'ok' }

  static getDerivedStateFromError(error: unknown): State {
    const msg = error instanceof Error ? error.message : String(error ?? '')
    // Se é recuperável e o anti-loop permite, vamos pra fase 'recovering'
    // (o reload é disparado em componentDidCatch, fora do render — efeitos
    // colaterais não podem ocorrer em getDerivedStateFromError).
    if (isRecoverableError(msg)) return { phase: 'recovering' }
    return { phase: 'failed' }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    const err = error instanceof Error ? error : undefined
    const msg = err ? err.message : String(error ?? '')
    const stack = err?.stack
    // Observabilidade (prod-safe): registra o stack REAL do erro — não só o
    // componentStack do React — no console E num global inspecionável via CDP.
    // No .ipk de prod o bundle é minificado e o boundary "engole" o throw
    // (recovery silencioso ou fallback), então o stack do crash nunca aparece.
    // Isto é a única forma de diagnosticar um crash que só reproduz no device
    // (ex.: Chr53 da TV). O console da TV não é visível ao usuário e o global é
    // inofensivo (só leitura de diagnóstico). Vale pra QUALQUER crash futuro.
    console.error('[recovery]', msg, stack)
    try {
      ;(
        window as unknown as {
          __lastRecoveryError?: { message: string; stack: string | null; componentStack: string | null; ts: number }
        }
      ).__lastRecoveryError = {
        message: msg,
        stack: stack ?? null,
        componentStack: info.componentStack ?? null,
        ts: Date.now(),
      }
    } catch {
      // window pode não existir (SSR/teste) — diagnóstico é best-effort.
    }
    console.error('[recovery-boundary] erro capturado:', msg, info.componentStack)
    if (this.state.phase === 'recovering') {
      // Dispara recovery COM guard anti-loop. Se suprimido (recovery recente),
      // cai pro fallback visível em vez de loopar reload.
      const fired = runRecoveryIfNotRecent(`render error: ${msg.slice(0, 80)}`)
      if (!fired) this.setState({ phase: 'failed' })
    }
  }

  override render(): ReactNode {
    if (this.state.phase === 'recovering') {
      // Recovery silencioso em curso → spinner enquanto a página recarrega.
      return <LoadingScreen />
    }
    if (this.state.phase === 'failed') {
      return <RecoveryFallback />
    }
    return this.props.children
  }
}

// Classe dedicada ao botão do fallback. Usada SÓ pra ancorar a regra de foco
// abaixo — NÃO depende de tokens.css.
const RECOVERY_BUTTON_CLASS = 'recovery-fallback-reload'

// Anel de foco robusto pra tela de RECUPERAÇÃO de crash. Em Tizen/webOS o UA
// reseta `outline:0`, então o foco do D-pad ficaria invisível (viola §5B.3
// "foco sempre visível"). Como esta é uma tela de fallback de crash, o
// tokens.css pode NÃO ter carregado (o bundle pode ter falhado) — então o anel
// é hardcoded (#fff), sem `var(--...)` nem classe externa. `outline-offset`
// afasta o anel branco do border roxo do botão pra ficar nítido em qualquer TV.
// :focus SOZINHO (sem :focus-visible): no Chr38/53 (WebView das TVs) o pseudo
// :focus-visible é DESCONHECIDO e invalida a LISTA de seletores inteira (CSS
// error-recovery), apagando o anel — o oposto do que esta tela de recuperação
// de crash precisa. Nesta tela de 1 botão só, mostrar o anel já no :focus é
// aceitável e garante visibilidade em qualquer TV.
const RECOVERY_FOCUS_CSS = `
.${RECOVERY_BUTTON_CLASS}:focus {
  outline: 3px solid #fff;
  outline-offset: 3px;
}
`

/** Fallback visível (funcional pra usar i18n via hook). Botão nativo focável. */
function RecoveryFallback() {
  const { t } = useTranslation()

  // O OK do controle é DESTA tela. Esta é a única superfície do app inteiro
  // nesse instante (a árvore do app já desmontou), e ela tem UMA ação — então
  // ser dona da tecla é o contrato correto, não um atalho. Ver lib/ok-key-owner.
  useOkKeyOwner(() => void runRecovery('user reload from recovery fallback (OK key)'))

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // Compat Chr38 (§2.1 de docs/specs/tv-compat.md): `gap` em flex é
        // Chr84+ — vira margin-top em cada filho MENOS o primeiro. Os flex
        // items reais são só h1/p/button: o <style> abaixo é `display: none`
        // pela UA stylesheet, logo não gera caixa e não conta como item (era
        // ignorado pelo gap também — paridade preservada).
        padding: '2rem',
        textAlign: 'center',
        // Hardcoded (não `var(--app-bg, #0a0a1a)`) — MESMA razão do anel de
        // foco abaixo: esta é a tela de recuperação de crash, tokens.css
        // pode não ter carregado. Mas no Chromium 38 (piso do produto,
        // webOS 3.5/2017) a razão é mais forte que "pode não ter carregado":
        // var() é sintaxe INVÁLIDA nesse engine (não suporta custom
        // properties, Chr49+) — `background: var(--x, fallback)` não "cai
        // pro fallback visualmente", a declaração INTEIRA é descartada e a
        // propriedade fica sem valor nenhum. Hardcoding os MESMOS valores
        // que já eram o fallback (#0a0a1a/#fff, iguais a --app-bg/--app-fg
        // em packages/ui/src/tokens.css) fecha esse buraco sem mudar nada
        // visualmente em nenhum device — achado pelo gate de CSS emitido
        // (apps/tv/scripts/compat/check-emitted-css.mjs) durante a PAREDE
        // de achatamento de CSS por capability.
        background: '#0a0a1a',
        color: '#fff',
      }}
    >
      {/* Regra de foco inline: garante o anel sem depender de tokens.css. */}
      <style>{RECOVERY_FOCUS_CSS}</style>
      {/* 1º flex item: sem margin-top (equivale ao 1º filho no gap). */}
      <h1 style={{ fontSize: '2rem', margin: 0 }}>{t('appError.title')}</h1>
      <p style={{ fontSize: '1.25rem', maxWidth: '36rem', opacity: 0.85, margin: '1.5rem 0 0' }}>
        {t('appError.body')}
      </p>
      <button
        type="button"
        autoFocus
        className={RECOVERY_BUTTON_CLASS}
        // Recarregar PURO pode remostrar o fallback se o cache HTTP ainda tem o
        // index velho. runRecovery (skipReload=false) faz unregister do SW +
        // clear caches ANTES do reload — assertivo no caso raro. Clique é ação
        // manual do usuário, então forçar é aceitável; runRecovery tem seus
        // próprios guards anti-loop (recoveryInProgress) pra não loopar.
        onClick={() => void runRecovery('user reload from recovery fallback')}
        style={{
          fontSize: '1.25rem',
          padding: '0.75rem 2rem',
          borderRadius: '0.5rem',
          // Hardcoded — ver comentário grande na `background`/`color` do
          // container acima (mesma razão: var() é sintaxe inválida no
          // Chromium 38, não um fallback silencioso).
          border: '2px solid #7c3aed',
          background: '#7c3aed',
          color: '#fff',
          cursor: 'pointer',
          marginTop: '1.5rem',
        }}
      >
        {t('appError.reload')}
      </button>
    </div>
  )
}
